<!--
  An editor for utterances, and their synonyms and entities

  Props:
  - value: the example value
      ex: "my name is {John:name}"
  - segments: array of segments, generated by ElUtteranceInput and synced
      ex: [{
            key: "0.345345", // random
            type: 'slot',
            text: 'John',
            name: 'name',
          }, ...]
  - prop: the prefix for el-form-item
  - rules: extra rules for the example
  - localVariables: autocomplete for @var variables
  - active: if should open slidedown
  - newInput: for a new input, without slidedown
  - placeholder: the text to display for an empty example

  Emits:
  - @input(value): value changed
  - @update:segments(segments): segments changed
  - @update:text(key, text): the name of the segment "key" has chaned
  - @activate(active): if should activate the item
  - @delete: should delete the item
-->

<template>
  <div @click="handleActivate">
    <el-form-sub ref="form"
      :prop="prop"
      :model="{value}"
      @click="handleActivate"
      v-on="$listeners"
      label-width="120px"
      :hide-required-asterisk="true">
      <!-- normal item -->
      <comparator-filter>
        <template v-slot="{ compare, index }">
          <div>
            <comparable :prop="x => stringify(x.value)"
              :required="!newInput"
              :rules="exampleRules">
              <template v-slot="{ attrs, status, view, compare }">
                <el-form-small-item :class="'comparable-' + status"
                  :prop="view && 'value'"
                  v-bind="attrs"
                  field="example">
                  <el-button-group>
                    <!-- button to open the slidedown -->
                    <tooltip-button v-if="newInput"
                      placement="bottom-start">
                      Write a training sentence.<br/>
                      Specific parts of the texts can be selected to highlight a <em>slot</em>.
                    </tooltip-button>
                    <el-tooltip v-else
                      effect="light" placement="left" :tabindex="-1"
                      :content="'# ' + index">
                      <el-button type="info" style="padding: 12px"
                        :disabled="compare"
                        :icon="!formValid ? 'el-icon-error' : !slots.length ? 'el-icon-minus' : active ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"
                        @click.stop="handleActivate(!active && formValid)"
                        :tabindex="-1">
                      </el-button>
                    </el-tooltip>
                    <!-- example input -->
                    <el-example-input
                      ref="input"
                      v-on="$listeners"
                      :disabled="compare"
                      :placeholder="placeholder"
                      :value="value"
                      :segments="copySegments"
                      @update:segments="handleSegments"
                      @selected="handleSelected">
                    </el-example-input>
                    <!-- delete button -->
                    <el-button :disabled="newInput || compare"
                      :icon="newInput ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                      @click="handleDelete"
                      :tabindex="-1">
                    </el-button>
                  </el-button-group>
                </el-form-small-item>
              </template>
            </comparable>
            <!-- slidedown -->
            <el-collapse-transition v-if="!newInput && activated">
              <el-form-sub label-width="120px"
                v-show="opened"
                prop="value"
                :model="{slots}"
                @valid="val => formValid = val">
                <fieldset>
                  <legend>Slots</legend>
                  <!-- "table" headers -->
                  <input-headers-group>
                    <input-header>Text</input-header>
                    <input-header>name</input-header>
                    <input-header width="56px"></input-header>
                  </input-headers-group>
                  <!-- all the entities and synonyms -->
                  <el-form-errors
                    v-for="(slot, index) in slots" :key="slot.key">
                    <el-button-group style="width: 100%">
                      <!-- name input -->
                      <el-form-small-item
                        :prop="'slots.'+index+'.text'"
                        :required="!slot.temporary"
                        field="text"
                        :rules="[ReadableValidator, SlotTextValidator]">
                        <el-input
                          :value="slot.text"
                          :disabled="compare"
                          @input="(val) => handleText(slot, val)"
                          :tabindex="opened ? '0' : '-1'">
                        </el-input>
                      </el-form-small-item>
                      <!-- synonym or slot select -->
                      <el-form-small-item
                        :prop="'slots.'+index+'.name'"
                        :required="!slot.temporary"
                        field="name"
                        :rules="[VariableValidator, duplicateRule(slot.key)]">
                        <el-entity-autocomplete
                          :value="slot.name"
                          :disabled="compare"
                          @input="(val) => handleName(slot, val)"
                          :suggestions="slotSuggestions.filter(s => s.used !== slot.key)"
                          :styleObj="{backgroundColor: slotColors ? slotColors[slot.value.name] : slot.color}"
                          :tabindex="opened ? 0 : -1">
                        </el-entity-autocomplete>
                      </el-form-small-item>
                      <!-- add or delete button for synonyms -->
                      <el-button
                        :icon="slot.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                        :disabled="compare || slot.temporary"
                        @click="$refs.input.unreference(slot.key)"
                        :tabindex="-1">
                      </el-button>
                    </el-button-group>
                  </el-form-errors>
                </fieldset>
              </el-form-sub>
            </el-collapse-transition>
          </div>
        </template>
      </comparator-filter>
    </el-form-sub>
  </div>
</template>

<script>
import {
  VariableValidator,
  ReadableValidator,
  SlotTextValidator,
  PreExampleValidator,
  ExampleValidator,
} from '../../utils/validators.js'

import ElExampleInput, { stringify } from '../element/ElExampleInput.vue'
import ElEntityAutocomplete from '../element/ElEntityAutocomplete.vue'

export default {
  name: 'ExampleEditor',
  components: {
    ElExampleInput,
    ElEntityAutocomplete,
  },
  props: {
    value: String,
    // array of segments, generated by ElUtteranceInput and synced
    segments: Array,
    active: { // if should open slidedown
      type: Boolean,
      default: true,
    },
    newInput: Boolean, // for a new input, without slidedown
    placeholder: String, // the text to display for an empty utterance

    prop: String, // the prefix for el-form-item
    rules: [Object, Array], // extra rules for the utterance
    slotSuggestions: Array, // autocomplete for @var variables
    slotColors: Object, // color of eahc slot name, if not updated by setColors
  },
  data () {
    return {
      newValue: '', // value for a new item
      copySegments: [], // a copy of the ElUtteranceInput.segments
      selection: null, // from ElUtteranceInput, the selected text (for synonyms)
      formValid: true, // if the form is valid
      activated: false, // optimisation to avoid loading the slidedown
      stringify, // ElUtteranceInput.stringify, for comparable entity values

      VariableValidator,
      ReadableValidator,
      SlotTextValidator,
    }
  },
  computed: {
    // rules for the example
    exampleRules () {
      return [ PreExampleValidator, ExampleValidator ].concat(this.rules || [])
    },
    // an array of all the the slots and synonyms segments
    // with eventually the temporary synonym from the selection
    slots () {
      let out = this.copySegments.filter(seg => seg.type !== 'text')
      if (this.selection) out.push(this.selection)
      return out
    },
    // if should open the slider
    opened () {
      return this.active && !!this.slots.length || !this.formValid
    },
  },
  methods: {
    // focus the utterance input
    focus () {
      this.$refs.input.focus()
      this.handleActivate()
    },
    // blur the utterance input
    blur () {
      this.$refs.input.blur()
    },
    // segments are updated by the utterance input
    handleSegments (val) {
      this.copySegments = val
    },
    // something has been selected in the utterance input
    // create a temporary segment for a new synonym
    handleSelected (val) {
      this.selection = val && {
        ...val,
        name: '',
        type: 'synonym',
        temporary: true,
      }
    },
    // emit activate
    handleActivate (activate) {
      if (this.newInput) return
      this.$emit('activate', activate !== false)
    },
    // emit delete
    handleDelete () {
      this.$emit('delete')
    },
    // the text of an slot or a synonym has been changed
    // emit if an slot , or just change the input if a synonym
    handleText (slot, text) {
      this.setText(slot.key, text, slot.temporary)
    },
    // change the text of a segment
    setText (key, val, temp) {
      // the temporary segment
      if (temp) {
        this.selection.text = val
      } else { // other segment, update the utterance input
        this.$refs.input.setText(key, val)
      }
    },
    // the entityid or synonymid has been changed
    // emit if an slot, or just change the input if a synonym
    handleName (slot, name) {
      this.setName(slot.key, name, slot.temporary)
    },
    // change the entityid or synonymid or a segment
    setName (key, val, temp) {
      val = val || null
      // the temporary segment, insert a new synonym in the utterance input
      if (temp) {
        let sel = this.selection
        sel.name = val
        if (!val) return
        this.selection = null
        this.$refs.input.insert(sel)
      } else { // other segment, update the utterance input
        this.$refs.input.setName(key, val)
      }
    },
    // change the entityid or synonymid or a segment
    setColor (key, val) {
      this.$refs.input.setColor(key, val)
    },
    // change the entityid or synonymid or a segment
    setColors (val) {
      this.$refs.input.setColors(val)
    },
    // a validator specifically for checking duplicate slot name
    duplicateRule (key) {
      return {
        validator: (rule, value, callback) => {
          callback(this.checkDuplicateName(value, key))
        },
      }
    },
    // used by duplicateRule, look for duplicate slot name
    checkDuplicateName (name, key) {
      for (let seg of this.slots) {
        if (seg.name === name && seg.key !== key) {
          return new Error('Slot defined multiple times')
        }
      }
    },
    validate (callback) {
      return this.$refs.form.validate(callback)
    },
  },
  // init copySegments and activated
  created () {
    this.copySegments = this.segments || []
    this.activated = this.active
  },
  watch: {
    // when eventually active, switch activated to true
    opened (active) {
      if (active) this.activated = true
    },
  },
}
</script>
